import React from "react";
import { Link } from "react-router-dom";
import SellerAvatar from "@/components/SellerAvatar";

/**
 * HeadlinerStage — top 3 of the week billed like a Coachella poster.
 * #1 huge, #2 medium, #3 small. Dark navy stage with animated
 * spotlight beams swinging back and forth. Click any name to enter
 * their store.
 *
 * This is the most brag-worthy of all the spotlight visuals: sellers
 * literally screenshot the poster to share. Designed for that.
 */
export default function HeadlinerStage({ data }) {
  const acts = (data?.headliners || []).slice(0, 3);
  if (acts.length < 1) return null;

  const [first, second, third] = acts;

  return (
    <section
      className="relative bg-[#070a15] text-white py-16 sm:py-24 overflow-hidden"
      data-testid="beacon-headliner-stage"
    >
      {/* Swinging stage lights */}
      <div className="absolute inset-0 stage-light stage-light-left pointer-events-none" />
      <div className="absolute inset-0 stage-light stage-light-right pointer-events-none" />

      {/* Vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(252,211,77,0.22),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <p className="overline text-amber-300/80 mb-2">Tonight's Headliners</p>
        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-10">
          {data.category_label}
        </p>

        {first && (
          <Link
            to={`/seller/${first.user_id}`}
            data-testid="headliner-1"
            className="block group"
          >
            <div className="flex justify-center mb-3">
              <SellerAvatar
                picture={first.picture}
                name={first.username}
                size={140}
                className="ring-4 ring-amber-300 shadow-[0_0_60px_rgba(252,211,77,0.4)]"
              />
            </div>
            <h2 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95] group-hover:text-amber-200 transition">
              @{first.username}
            </h2>
            {first.is_founder && (
              <span className="inline-block mt-2 text-[10px] uppercase tracking-widest font-bold text-amber-300/90 border border-amber-300/30 rounded-full px-3 py-1">
                Founder Seller
              </span>
            )}
          </Link>
        )}

        {(second || third) && (
          <div className="mt-10 flex items-end justify-center gap-8 sm:gap-12">
            {second && (
              <Link
                to={`/seller/${second.user_id}`}
                data-testid="headliner-2"
                className="group"
              >
                <SellerAvatar
                  picture={second.picture}
                  name={second.username}
                  size={88}
                  className="ring-2 ring-white/40 mx-auto"
                />
                <p className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-2 group-hover:text-amber-200">
                  @{second.username}
                </p>
              </Link>
            )}
            {third && (
              <Link
                to={`/seller/${third.user_id}`}
                data-testid="headliner-3"
                className="group"
              >
                <SellerAvatar
                  picture={third.picture}
                  name={third.username}
                  size={64}
                  className="ring-2 ring-white/30 mx-auto"
                />
                <p className="font-display font-black text-lg sm:text-xl tracking-tighter mt-2 group-hover:text-amber-200">
                  @{third.username}
                </p>
              </Link>
            )}
          </div>
        )}

        <p className="mt-12 text-xs uppercase tracking-[0.3em] text-white/30">
          ·  Bidorah  ·  {data.today}  ·  the beacon  ·
        </p>
      </div>
    </section>
  );
}
